import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "../../pages/homepage.css";
import "./homepageEditor.css";
import {
  FaWrench, FaBoxOpen, FaHardHat,
  FaClipboardCheck, FaSatelliteDish, FaShip,
} from "react-icons/fa";
import { CiEdit } from "react-icons/ci";

/* ─── API Config ─── */
// const BASE_URL = "https://api.jeyshawnng.com";
const BASE_URL = "https://api.jeyshawnng.com";
const API_BASE  = `${BASE_URL}/api/homepage`;
const UPLOAD_URL = `${BASE_URL}/api/upload`;
const SERVER_ORIGIN = BASE_URL;

/* ─── Local cache (cache-first, DB-driven) ─── */
const CACHE_KEY = "jeyshawn_homepage_data";

const getCachedData = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedData = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage full/unavailable — fail silently, page still works
  }
};

const SERVICE_ICONS = [
  <FaWrench />, <FaBoxOpen />, <FaHardHat />,
  <FaClipboardCheck />, <FaSatelliteDish />, <FaShip />,
];

/* ─── Empty scaffold — used as the base for a fresh modal (no DB data) ─── */
const EMPTY_DATA = {
  heroSlides: Array.from({ length: 6 }, () => ({ title: "", bg: "" })),
  announcement: "",
  about: {
    heading: "",
    paragraph1: "",
    paragraph2: "",
    paragraph3: "",
    statHours: "",
    statHoursLabel: "",
    statProjects: "",
    statProjectsLabel: "",
    image: "",
    videoUrl: "",
  },
  servicesSection: { title: "" },
  services: Array.from({ length: 6 }, () => ({ title: "", desc: "", href: "" })),
  clientsSection: { title: "", subtitle: "" },
  clients: [],
  coreValues: {
    label: "",
    heading: "",
    items: [{ name: "", desc: "" }],
  },
};

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${SERVER_ORIGIN}${url}`;
};

/* ═══════════════════════════════
   IMAGE UPLOAD FIELD
═══════════════════════════════ */
function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview, setPreview]     = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await axios.post(UPLOAD_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.success) throw new Error(data.message || "Upload failed");
      onUploaded(data.url);
      setPreview(resolveUrl(data.url));
    } catch (err) {
      setError("Upload failed. Try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && (
            <div className="editor-img-uploading">
              <div className="editor-loading__spinner editor-loading__spinner--sm" />
              <span>Uploading...</span>
            </div>
          )}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10 MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ═══════════════════════════════
   EDIT MODAL
   • Receives `dbData` (raw API response, may be null)
   • If null → all fields are blank (EMPTY_DATA)
   • If populated → fields reflect DB values exactly
═══════════════════════════════ */
function EditModal({ dbData, onSave, onClose, saving }) {
  // Deep-merge DB data on top of EMPTY_DATA so every key always exists,
  // but values come from DB when available and are empty strings otherwise.
  const buildDraft = () => {
    if (!dbData) return JSON.parse(JSON.stringify(EMPTY_DATA));

    // Merge top-level scalar
    const base = JSON.parse(JSON.stringify(EMPTY_DATA));

    base.announcement = dbData.announcement ?? "";

    // heroSlides — keep exactly 6 slots
    if (Array.isArray(dbData.heroSlides)) {
      dbData.heroSlides.slice(0, 6).forEach((s, i) => {
        base.heroSlides[i] = { title: s.title ?? "", bg: s.bg ?? "" };
      });
    }

    // about
    if (dbData.about) {
      Object.keys(base.about).forEach((k) => {
        if (dbData.about[k] !== undefined) base.about[k] = dbData.about[k];
      });
    }

    // servicesSection
    if (dbData.servicesSection?.title !== undefined)
      base.servicesSection.title = dbData.servicesSection.title;

    // services — keep up to 6, pad with empty objects
    if (Array.isArray(dbData.services)) {
      dbData.services.slice(0, 6).forEach((s, i) => {
        base.services[i] = {
          title: s.title ?? "",
          desc:  s.desc  ?? "",
          href:  s.href  ?? "",
        };
      });
    }

    // clientsSection
    if (dbData.clientsSection) {
      if (dbData.clientsSection.title    !== undefined) base.clientsSection.title    = dbData.clientsSection.title;
      if (dbData.clientsSection.subtitle !== undefined) base.clientsSection.subtitle = dbData.clientsSection.subtitle;
    }

    // clients — fully dynamic list, no fixed count
    if (Array.isArray(dbData.clients)) {
      base.clients = dbData.clients.map((c) => ({
        name: c.name ?? "",
        logo: c.logo ?? "",
      }));
    } else {
      base.clients = [];
    }

    // coreValues
    if (dbData.coreValues) {
      if (dbData.coreValues.label   !== undefined) base.coreValues.label   = dbData.coreValues.label;
      if (dbData.coreValues.heading !== undefined) base.coreValues.heading = dbData.coreValues.heading;
      if (Array.isArray(dbData.coreValues.items)) {
        base.coreValues.items = dbData.coreValues.items.map((item) => ({
          name: item.name ?? "",
          desc: item.desc ?? "",
        }));
      }
    }

    return base;
  };

  const [draft, setDraft] = useState(buildDraft);

  /* helpers */
  const setHero    = (i, f, v) => setDraft(d => { const s = [...d.heroSlides]; s[i] = { ...s[i], [f]: v }; return { ...d, heroSlides: s }; });
  const setService = (i, f, v) => setDraft(d => { const s = [...d.services]; s[i] = { ...s[i], [f]: v }; return { ...d, services: s }; });
  const setCvItem  = (i, f, v) => setDraft(d => { const items = [...d.coreValues.items]; items[i] = { ...items[i], [f]: v }; return { ...d, coreValues: { ...d.coreValues, items } }; });
  const addCvItem  = () => setDraft(d => ({ ...d, coreValues: { ...d.coreValues, items: [...d.coreValues.items, { name: "", desc: "" }] } }));
  const removeCvItem = (i) => setDraft(d => { const items = d.coreValues.items.filter((_, idx) => idx !== i); return { ...d, coreValues: { ...d.coreValues, items } }; });

  /* clients — add / update / delete */
  const setClient    = (i, f, v) => setDraft(d => { const c = [...d.clients]; c[i] = { ...c[i], [f]: v }; return { ...d, clients: c }; });
  const addClient    = () => setDraft(d => ({ ...d, clients: [...d.clients, { name: "", logo: "" }] }));
  const removeClient = (i) => setDraft(d => ({ ...d, clients: d.clients.filter((_, idx) => idx !== i) }));

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit Homepage Content</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* ── HERO SLIDES ── */}
          <p className="editor-section__title">Hero Slides</p>
          {draft.heroSlides.map((slide, i) => (
            <div key={i} className="editor-group">
              <p className="editor-group__label">Slide {i + 1}</p>
              <label>Title</label>
              <input
                value={slide.title}
                placeholder="Enter slide title"
                onChange={e => setHero(i, "title", e.target.value)}
              />
              <ImageUploadField
                label="Background Image"
                currentUrl={slide.bg}
                onUploaded={url => setHero(i, "bg", url)}
              />
            </div>
          ))}

          {/* ── ANNOUNCEMENT ── */}
          <p className="editor-section__title">Announcement Banner</p>
          <div className="editor-group">
            <label>Banner Text</label>
            <textarea
              rows={2}
              value={draft.announcement}
              placeholder="Enter announcement text"
              onChange={e => setDraft(d => ({ ...d, announcement: e.target.value }))}
            />
          </div>

          {/* ── ABOUT ── */}
          <p className="editor-section__title">About Section</p>
          <div className="editor-group">
            <label>Heading</label>
            <input
              value={draft.about.heading}
              placeholder="Enter section heading"
              onChange={e => setDraft(d => ({ ...d, about: { ...d.about, heading: e.target.value } }))}
            />
            <label>Paragraph 1</label>
            <textarea rows={4} value={draft.about.paragraph1} placeholder="Enter first paragraph" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, paragraph1: e.target.value } }))} />
            <label>Paragraph 2</label>
            <textarea rows={3} value={draft.about.paragraph2} placeholder="Enter second paragraph" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, paragraph2: e.target.value } }))} />
            <label>Paragraph 3</label>
            <textarea rows={3} value={draft.about.paragraph3} placeholder="Enter third paragraph" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, paragraph3: e.target.value } }))} />
            <label>Stat — Man Hours Number</label>
            <input value={draft.about.statHours} placeholder="e.g. 105,120" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, statHours: e.target.value } }))} />
            <label>Stat — Man Hours Label</label>
            <input value={draft.about.statHoursLabel} placeholder="e.g. Man Hours With Zero Fatality" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, statHoursLabel: e.target.value } }))} />
            <label>Stat — Projects Number</label>
            <input value={draft.about.statProjects} placeholder="e.g. 23" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, statProjects: e.target.value } }))} />
            <label>Stat — Projects Label</label>
            <input value={draft.about.statProjectsLabel} placeholder="e.g. With 100% Completion Rate" onChange={e => setDraft(d => ({ ...d, about: { ...d.about, statProjectsLabel: e.target.value } }))} />
            <label>YouTube Embed URL</label>
            <input
              value={draft.about.videoUrl}
              placeholder="https://www.youtube.com/embed/..."
              onChange={e => setDraft(d => ({ ...d, about: { ...d.about, videoUrl: e.target.value } }))}
            />
            <ImageUploadField
              label="About Section Image"
              currentUrl={draft.about.image}
              onUploaded={url => setDraft(d => ({ ...d, about: { ...d.about, image: url } }))}
            />
          </div>

          {/* ── SERVICES ── */}
          <p className="editor-section__title">Services Section</p>
          <div className="editor-group">
            <label>Section Title</label>
            <input
              value={draft.servicesSection.title}
              placeholder="e.g. What We Do"
              onChange={e => setDraft(d => ({ ...d, servicesSection: { ...d.servicesSection, title: e.target.value } }))}
            />
          </div>
          {draft.services.map((svc, i) => (
            <div key={i} className="editor-group">
              <p className="editor-group__label">Service {i + 1}</p>
              <label>Title</label>
              <input value={svc.title} placeholder="Service title" onChange={e => setService(i, "title", e.target.value)} />
              <label>Description</label>
              <textarea rows={3} value={svc.desc} placeholder="Brief description of this service" onChange={e => setService(i, "desc", e.target.value)} />
              <label>Link (href)</label>
              <input value={svc.href} placeholder="/service-page" onChange={e => setService(i, "href", e.target.value)} />
            </div>
          ))}

          {/* ── CLIENTS SECTION ── */}
          <p className="editor-section__title">Clients Section</p>
          <div className="editor-group">
            <label>Section Title</label>
            <input
              value={draft.clientsSection.title}
              placeholder="e.g. Our Clients"
              onChange={e => setDraft(d => ({ ...d, clientsSection: { ...d.clientsSection, title: e.target.value } }))}
            />
            <label>Subtitle</label>
            <textarea
              rows={2}
              value={draft.clientsSection.subtitle}
              placeholder="Short subtitle describing your client relationships"
              onChange={e => setDraft(d => ({ ...d, clientsSection: { ...d.clientsSection, subtitle: e.target.value } }))}
            />
          </div>

          <div className="editor-group">
            <p className="editor-group__label">Client Logos ({draft.clients.length})</p>
            {draft.clients.length === 0 && (
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                No clients yet. Click "Add Client" to add your first logo.
              </p>
            )}
            {draft.clients.map((c, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  paddingBottom: 12,
                  borderBottom: "1px solid #e5e7eb",
                  marginBottom: 10,
                }}
              >
                <div className="editor-value-row">
                  <input
                    placeholder="Client name (e.g. Chevron)"
                    value={c.name}
                    onChange={e => setClient(i, "name", e.target.value)}
                  />
                  <button
                    className="editor-value-row__remove"
                    onClick={() => removeClient(i)}
                    title="Remove client"
                    type="button"
                  >
                    ✕
                  </button>
                </div>
                <ImageUploadField
                  label="Logo"
                  currentUrl={c.logo}
                  onUploaded={url => setClient(i, "logo", url)}
                />
              </div>
            ))}
            <button className="editor-add-btn" onClick={addClient} type="button">+ Add Client</button>
          </div>

          {/* ── CORE VALUES ── */}
          <p className="editor-section__title">Core Values Section</p>
          <div className="editor-group">
            <label>Section Label</label>
            <input
              value={draft.coreValues.label}
              placeholder="e.g. Core Values"
              onChange={e => setDraft(d => ({ ...d, coreValues: { ...d.coreValues, label: e.target.value } }))}
            />
            <label>Heading</label>
            <input
              value={draft.coreValues.heading}
              placeholder="e.g. Here are the values that drive us"
              onChange={e => setDraft(d => ({ ...d, coreValues: { ...d.coreValues, heading: e.target.value } }))}
            />
          </div>
          <div className="editor-group">
            <p className="editor-group__label">Value Items</p>
            {draft.coreValues.items.map((item, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 10, borderBottom: "1px solid #e5e7eb", marginBottom: 4 }}>
                <div className="editor-value-row">
                  <input placeholder="Value name (e.g. Integrity)" value={item.name} onChange={e => setCvItem(i, "name", e.target.value)} />
                  <button className="editor-value-row__remove" onClick={() => removeCvItem(i)} title="Remove">✕</button>
                </div>
                <textarea
                  rows={3}
                  placeholder="Describe this value..."
                  value={item.desc}
                  onChange={e => setCvItem(i, "desc", e.target.value)}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                />
              </div>
            ))}
            <button className="editor-add-btn" onClick={addCvItem}>+ Add Value</button>
          </div>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ═══════════════════════════════
   SERVICE CARD
═══════════════════════════════ */
function ServiceCard({ icon, title, desc, href }) {
  return (
    <div className="service-card">
      <div className="service-card__icon">{icon}</div>
      <h3>{title}</h3>
      <p>{desc}</p>
      <a href={href} className="btn btn--navy">Learn More</a>
    </div>
  );
}

/* ═══════════════════════════════
   HOMEPAGE
   Data comes only from the DB. On first ever visit
   (no cache) it loads from the API in the foreground.
   On return visits it renders instantly from
   localStorage, then silently re-checks the DB in
   the background (and on a 30s interval) and only
   updates the UI + cache if the DB data changed.
═══════════════════════════════ */
const Homepage = () => {
  // `pageData` — what the page renders AND what the edit modal is seeded with.
  // null = nothing to show yet (no cache, DB empty, or DB unreachable).
  const [pageData,   setPageData]   = useState(() => getCachedData());
  const [loading,    setLoading]    = useState(() => !getCachedData());
  const [fetchErr,   setFetchErr]   = useState(null);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [saveMsg,    setSaveMsg]    = useState(null);
  const [current,    setCurrent]    = useState(0);
  const [videoOpen,  setVideoOpen]  = useState(false);
  const [currentIndex,   setCurrentIndex]   = useState(0);
  const [isAutoPlaying,  setIsAutoPlaying]  = useState(true);
  const intervalRef = useRef(null);
  const itemsPerView = 6;

  const fetchPageData = async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
        setFetchErr(null);
      }

      const { data } = await axios.get(API_BASE);

      if (data.success && data.data && Object.keys(data.data).length > 0) {
        const cached = getCachedData();
        const changed = JSON.stringify(cached) !== JSON.stringify(data.data);
        if (changed) {
          setPageData(data.data);
          setCachedData(data.data);
        }
      } else if (!isBackground && !getCachedData()) {
        setPageData(null);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      // Background syncs, or foreground loads when we already have cached
      // data, fail silently so the page keeps showing what it has.
      if (!isBackground && !getCachedData()) {
        setPageData(null);
        setFetchErr("Could not load page data.");
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  /* ── Fetch on mount + background polling ── */
  useEffect(() => {
    const hasCache = !!getCachedData();
    fetchPageData(hasCache); // silent sync if we already have cached data to show

    const interval = setInterval(() => fetchPageData(true), 30000); // auto-check DB every 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (draft) => {
    try {
      setSaving(true);
      const { data } = await axios.put(API_BASE, draft);
      if (!data.success) throw new Error(data.message || "Save failed");
      setPageData(data.data);
      setCachedData(data.data);
      setModalOpen(false);
      showToast("Changes saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg) => { setSaveMsg(msg); setTimeout(() => setSaveMsg(null), 3500); };

  /* ── Hero slider ── */
  const slides = Array.isArray(pageData?.heroSlides) ? pageData.heroSlides : [];
  const go = (n) => { if (slides.length > 0) setCurrent((n + slides.length) % slides.length); };
  useEffect(() => {
    if (slides.length === 0) return;
    const t = setInterval(() => setCurrent(c => (c + 1) % slides.length), 8500);
    return () => clearInterval(t);
  }, [slides.length]);

  /* ── Clients carousel ── */
  // Only clients with a logo that actually resolves to a real image URL —
  // skip anything with a missing/broken logo instead of rendering a blank card.
  const clientList = Array.isArray(pageData?.clients)
    ? pageData.clients.filter(c => Boolean(resolveUrl(c?.logo)))
    : [];

  const totalSets = Math.max(1, Math.ceil(clientList.length / itemsPerView));
  const maxIndex  = totalSets - 1;
  const nextSlide = useCallback(() => setCurrentIndex(p => (p >= maxIndex ? 0 : p + 1)), [maxIndex]);
  const prevSlide = useCallback(() => setCurrentIndex(p => (p <= 0 ? maxIndex : p - 1)), [maxIndex]);
  useEffect(() => {
    setCurrentIndex(0); // reset position if the client list shrinks/grows
  }, [clientList.length]);
  useEffect(() => {
    if (isAutoPlaying && clientList.length > itemsPerView) {
      intervalRef.current = setInterval(nextSlide, 4000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isAutoPlaying, nextSlide, clientList.length]);

  /* ── Resolved assets ── */
  const slide      = slides[current];
  const getHeroBg  = (idx) => resolveUrl(slides[idx]?.bg);
  const aboutImage = resolveUrl(pageData?.about?.image);

  return (
    <>
      {/* Edit FAB */}
              <button
                title="Edit page"
                onClick={() => setModalOpen(true)}
                style={{ position: "absolute", top: 70, right: 20, zIndex: 10, background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "50%", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                <CiEdit size={24} color="#333" />
              </button>

      {saveMsg && <div className="editor-toast">{saveMsg}</div>}

      {loading && <div className="editor-loading"><div className="editor-loading__spinner" /></div>}

      {fetchErr && (
        <div className="editor-error-banner">
          {fetchErr}
          <button onClick={() => fetchPageData(false)}>Retry</button>
        </div>
      )}

      {/* Modal receives raw pageData — null means "show empty fields" */}
      {modalOpen && (
        <EditModal
          dbData={pageData}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
        />
      )}

      {pageData && (
        <>
          {/* ── HERO ── */}
          {slides.length > 0 && (
            <section className="hero" id="home">
              <div className="hero__bg" style={{ backgroundImage: `url(${getHeroBg(current)})` }} />
              <div className="hero__content">
                <h1>{slide?.title}</h1>
                <p>{slide?.sub}</p>
                <a href="#about" className="btn btn--orange">Read More</a>
              </div>
              <button className="hero__arrow hero__arrow--prev" onClick={() => go(current - 1)} aria-label="Previous slide">&#8249;</button>
              <button className="hero__arrow hero__arrow--next" onClick={() => go(current + 1)} aria-label="Next slide">&#8250;</button>
              <div className="hero__dots">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => go(i)} className={`hero__dot${i === current ? " hero__dot--active" : ""}`} aria-label={`Go to slide ${i + 1}`} />
                ))}
              </div>
            </section>
          )}

          {/* ── ANNOUNCEMENT ── */}
          {pageData.announcement && (
            <div className="announcement">
              <div className="announcement_carrier">
                <p>{pageData.announcement}</p>
                <a href="#about" className="btn btn--dark">Read More</a>
              </div>
            </div>
          )}

          {/* ── ABOUT ── */}
          <section
            className="section light_green"
            id="about"
            style={{
              ...(aboutImage ? { backgroundImage: `url(${aboutImage})` } : {}),
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="welcome__grid">
              <div className="welcome__text">
                <h2>{pageData.about?.heading}</h2>
                <p>{pageData.about?.paragraph1}</p>
                <p>{pageData.about?.paragraph2}</p>
                <p>{pageData.about?.paragraph3}</p>
                <section className="stats-wrap">
                  <div className="stat-block">
                    <div className="stat-number">{pageData.about?.statHours}</div>
                    <div className="stat-label">{pageData.about?.statHoursLabel}</div>
                  </div>
                  <div className="divider" aria-hidden="true" />
                  <div className="stat-block">
                    <div className="stat-number">{pageData.about?.statProjects} <span className="accent">PROJECTS</span></div>
                    <div className="stat-label">{pageData.about?.statProjectsLabel}</div>
                  </div>
                </section>
              </div>

              <div className="welcome__images">
                {aboutImage && <img src={aboutImage} alt="Oilfield worker" />}
                {pageData.about?.videoUrl && (
                  <button className="play-btn" onClick={() => setVideoOpen(true)}>
                    <span className="play-btn__icon">
                      <svg viewBox="0 0 10 12" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 0 L10 6 L0 12 Z" />
                      </svg>
                    </span>
                  </button>
                )}
              </div>

              {videoOpen && pageData.about?.videoUrl && (
                <div className="video-modal-overlay" onClick={e => e.target === e.currentTarget && setVideoOpen(false)}>
                  <div className="video-modal">
                    <button className="video-modal__close" onClick={() => setVideoOpen(false)}>✕</button>
                    <iframe
                      src={pageData.about.videoUrl}
                      allow="autoplay; encrypted-media; fullscreen"
                      allowFullScreen
                      title="Company Video"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ── SERVICES ── */}
          {Array.isArray(pageData.services) && pageData.services.length > 0 && (
            <section className="section section--light" id="services">
              <h2 className="section__title">{pageData.servicesSection?.title}</h2>
              <div className="services__grid">
                {pageData.services.map((s, i) => (
                  <ServiceCard key={i} icon={SERVICE_ICONS[i] || <FaWrench />} title={s.title} desc={s.desc} href={s.href} />
                ))}
              </div>
            </section>
          )}

          {/* ── CORE VALUES ── */}
          {Array.isArray(pageData.coreValues?.items) && pageData.coreValues.items.length > 0 && (
            <section className="section section--navy" id="values">
              <div className="cv-section">
                <div className="cv-label">{pageData.coreValues?.label}</div>
                <h2 className="cv-heading">{pageData.coreValues?.heading}</h2>
                <div className="cv-grid">
                  {pageData.coreValues.items.map((item, i) => (
                    <div key={i} className="cv-item">
                      <div className="cv-name">{item.name}</div>
                      <div className="cv-desc">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ── CLIENTS ── */}
          {clientList.length > 0 && (
            <section
              className="clients-section"
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <h2 className="clients__title">{pageData.clientsSection?.title}</h2>
              <p className="clients__subtitle">{pageData.clientsSection?.subtitle}</p>
              <div className="clients__carousel">
                <button className="clients__arrow clients__arrow--prev" onClick={prevSlide}>‹</button>
                <div className="clients__viewport">
                  <div className="clients__track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                    {clientList.map((c, i) => (
                      <div key={i} className="clients__card">
                        <img src={resolveUrl(c.logo)} alt={c.name} className="clients__logo-img" />
                      </div>
                    ))}
                  </div>
                </div>
                <button className="clients__arrow clients__arrow--next" onClick={nextSlide}>›</button>
              </div>
              <div className="clients__dots">
                {Array.from({ length: totalSets }).map((_, i) => (
                  <button
                    key={i}
                    className={`clients__dot ${i === currentIndex ? "clients__dot--active" : ""}`}
                    onClick={() => setCurrentIndex(i)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

    </>
  );
};

export default Homepage;